import {
  type Camera,
  type Group,
  Matrix4,
  OrthographicCamera,
  PerspectiveCamera,
  type Plane,
  Raycaster,
  Vector2,
  Vector3,
  Vector4,
  type WebGLRenderer,
} from 'three'
import type { IPlane } from '@/plugins/cad/module/CADModule/manager/DataManager'

/** 视口尺寸信息 */
export interface IViewSize {
  /** 视口宽度，单位 CSS 像素 */
  clientWidth: number
  /** 视口高度，单位 CSS 像素 */
  clientHeight: number
}

type RendererLike =
  | WebGLRenderer
  | {
      domElement: HTMLCanvasElement
      getSize?: (target: Vector2) => Vector2
    }

/**
 * CAD 私有 view 环境：dash scale 与「像素→世界」换算只需要这些尺寸信息。
 *
 * 由 backend surface / ViewRect 构造，调用方不再传入 `WebGLRenderer`——WebGPU 下没有该对象，
 * 且读取它会抛后端不匹配错误。单位与 `toRendererViewportRect` 的输入一致（CSS 像素）。
 */
export interface CadViewEnvironment {
  readonly viewWidth: number
  readonly viewHeight: number
  readonly pixelRatio: number
}

/** 判断参数是否已经是 CAD view 环境（而非 legacy renderer）。 */
export function isCadViewEnvironment(source: unknown): source is CadViewEnvironment {
  return (
    typeof source === 'object' && source !== null && 'viewWidth' in source && 'viewHeight' in source
  )
}

/** 从 view 环境读取尺寸；renderer 输入保留给尚未迁移的 WebGL 调用方。 */
function resolveViewDimensions(source?: CadViewEnvironment | RendererLike | null) {
  if (isCadViewEnvironment(source)) {
    return { width: source.viewWidth, height: source.viewHeight }
  }
  return getRendererDimensions(source)
}

const rendererSize = new Vector2()
const rendererViewport = new Vector4()
const ndcPoint = new Vector2()
const raycaster = new Raycaster()
const pixelWorldPointA = new Vector3()
const pixelWorldPointB = new Vector3()
const cameraPosition = new Vector3()
const cameraDirection = new Vector3()
const referencePointDelta = new Vector3()
const fallbackWorldPointA = new Vector3()
const fallbackWorldPointB = new Vector3()

function getRendererDimensions(renderer?: RendererLike | null) {
  if (!renderer) {
    return { width: 0, height: 0 }
  }

  // WebGLRenderer.getViewport 返回逻辑像素；多视口不能使用整个 canvas 的尺寸。
  if ('getViewport' in renderer && typeof renderer.getViewport === 'function') {
    const viewport = renderer.getViewport(rendererViewport)
    if (viewport.z > 0 && viewport.w > 0) return { width: viewport.z, height: viewport.w }
  }

  const { domElement } = renderer
  const size = renderer.getSize?.(rendererSize)
  const width = domElement?.clientWidth || domElement?.width || size?.x || 0
  const height = domElement?.clientHeight || domElement?.height || size?.y || 0
  return { width, height }
}

function getPixelSizeOnPlane(
  camera: OrthographicCamera | PerspectiveCamera,
  plane: Plane,
  width: number
) {
  ndcPoint.set(0, 0)
  raycaster.setFromCamera(ndcPoint, camera)
  const pointA = raycaster.ray.intersectPlane(plane, pixelWorldPointA)
  if (!pointA) {
    return
  }

  ndcPoint.set(2 / width, 0)
  raycaster.setFromCamera(ndcPoint, camera)
  const pointB = raycaster.ray.intersectPlane(plane, pixelWorldPointB)
  if (!pointB) {
    return
  }

  const pixelSize = pointA.distanceTo(pointB)
  return Number.isFinite(pixelSize) && pixelSize > 0 ? pixelSize : undefined
}

function getPerspectivePixelSizeAtReferencePoint(
  camera: PerspectiveCamera,
  height: number,
  referencePoint?: Vector3
) {
  if (!referencePoint) {
    return
  }

  camera.getWorldPosition(cameraPosition)
  camera.getWorldDirection(cameraDirection)

  const depth = referencePointDelta.subVectors(referencePoint, cameraPosition).dot(cameraDirection)
  if (!Number.isFinite(depth) || depth <= camera.near) {
    return
  }

  const zoom = camera.zoom || 1
  const visibleHeight = (2 * Math.tan((camera.fov * Math.PI) / 360) * depth) / zoom
  const pixelSize = visibleHeight / height
  return Number.isFinite(pixelSize) && pixelSize > 0 ? pixelSize : undefined
}

function getPixelSizeByCameraType(
  camera: Camera,
  width: number,
  height: number,
  referencePoint?: Vector3
) {
  if (camera instanceof OrthographicCamera) {
    const zoom = camera.zoom || 1
    const pixelSize = Math.abs(camera.right - camera.left) / zoom / width
    return Number.isFinite(pixelSize) && pixelSize > 0 ? pixelSize : undefined
  }

  if (camera instanceof PerspectiveCamera) {
    return getPerspectivePixelSizeAtReferencePoint(camera, height, referencePoint)
  }
}

/**
 * 基于相机和渲染器计算指定像素半径在世界坐标中的尺寸。
 */
export const getPixelSizeInWorldByCamera = (
  camera: Camera,
  source: CadViewEnvironment | RendererLike,
  radius: number,
  options: { plane?: Plane; referencePoint?: Vector3 } = {}
): number => {
  const { width, height } = resolveViewDimensions(source)
  if (width <= 0 || height <= 0) {
    return radius
  }

  const supportsPlaneRaycast =
    camera instanceof OrthographicCamera || camera instanceof PerspectiveCamera
  const pixelSize =
    (options.plane && supportsPlaneRaycast
      ? getPixelSizeOnPlane(camera, options.plane, width)
      : undefined) ?? getPixelSizeByCameraType(camera, width, height, options.referencePoint)

  if (pixelSize !== undefined) {
    return pixelSize * radius
  }

  fallbackWorldPointA.set(0, 0, 0.5).unproject(camera)
  fallbackWorldPointB.set(2 / width, 0, 0.5).unproject(camera)
  return fallbackWorldPointA.distanceTo(fallbackWorldPointB) * radius
}

/**
 * 计算指定像素半径在世界坐标中的尺寸。
 *
 * @param camera - 当前使用的相机
 * @param viewSize - 视口尺寸，单位 CSS 像素
 * @param radius - 期望的屏幕空间半径，单位像素
 * @returns 对应的世界坐标尺寸；viewSize.clientWidth 为 0 时回退返回 radius
 */
export const getPixelSizeInWorld = (
  camera: Camera,
  viewSize: IViewSize,
  radius: number
): number => {
  const w = viewSize.clientWidth
  if (w === 0) {
    return radius
  }
  const v1 = new Vector3(0, 0, 0.5).unproject(camera)
  const v2 = new Vector3(2 / w, 0, 0.5).unproject(camera)

  return v1.distanceTo(v2) * radius
}

/**
 * 依据平面的原点、法线与 up 向量刷新 Group 的世界位姿。
 *
 * @param group - 需要对齐到平面的 Three.js 组
 * @param plane - 目标平面，提供 origin、normal、up
 */
export function updateGroupByPlane(group: Group, plane: IPlane) {
  const { origin, normal, up } = plane
  // 1. Z轴 = 法线 (确保指向屏幕外)
  const zAxis = normal.clone().normalize()
  // 2. X轴 = Up 叉乘 Z轴 (根据右手定则得出向右的方向)
  const xAxis = new Vector3().crossVectors(up, zAxis).normalize()
  // 3. Y轴 = Z轴 叉乘 X轴 (重新校准 Up，确保它垂直于 X 和 Z)
  const yAxis = new Vector3().crossVectors(zAxis, xAxis).normalize()
  // 4. 构建 4x4 矩阵
  const matrix = new Matrix4()
  matrix.makeBasis(xAxis, yAxis, zAxis)
  matrix.setPosition(origin)
  // 5. 应用到 Group
  group.quaternion.setFromRotationMatrix(matrix)
  group.position.copy(origin)
  group.updateMatrixWorld(true)
}
